// Production calendar, Script list tab (DESIGN rev 2.1 §3.1: Grid at medium density). Membership is derived (D-050):
// Ready = Skit / Mockumentary in Needs review or Approved with no shoot segments; Assigned = has unfilmed segments
// (Riff cards included). Readable by the whole tab set; write actions show for Producer / Power User / Admin only and
// the server enforces them regardless. Same calls: GET /api/tal (status, page, pageSize 200 on a focus jump, clientId,
// q, and on Ready availableOn / showHidden), PATCH /api/tal/:scriptId/remove | restore, PATCH /api/tal/lp/:id/notes.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, FileText, RefreshCw, Search, X } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import DatePicker from '../../ui/DatePicker';
import Grid from '../../ui/Grid';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import StageChip from '../../ui/StageChip';
import Tabs from '../../ui/Tabs';
import Tag from '../../ui/Tag';
import { useConfirm } from '../../ui/Confirm';
import { useToast } from '../../ui/Toast';
import { conditionOf, stageOf } from '../../ui/stages';
import { cx } from '../../ui/util';
import { clean } from '../../shell/copy';
import SchedulePlanModal from '../../components/SchedulePlanModal';
import { canAssignTalent, coverageReason, plural, shortDate } from './util';

const ROW_PX = 40;   // the Grid's medium row and group header (tokens --row-medium), for the focus jump
const READY_STAGES = ['Writing', 'Scripted'];
const stop = e => e.stopPropagation();

// A talent's initials as a jump to the Talent calendar; a broken booking reads as a danger badge with the reason.
function TalentLink({ label, title, broken, onClick }) {
  return (
    <button type="button" className="ui-link" title={title} onClick={e => { e.stopPropagation(); onClick?.(); }}>
      {broken ? <Badge tone="danger">{label}</Badge> : <Tag>{label}</Tag>}
    </button>
  );
}

function ShootDays({ days, onOpenShootDay }) {
  return days.map(d => (
    <button key={d} type="button" className="ui-link ui-num" title="Open this day on the Shoot schedule"
      onClick={e => { e.stopPropagation(); onOpenShootDay?.(d); }}>{shortDate(d)}</button>
  ));
}

export default function ScriptListView({ focus, onFocusConsumed, onOpenShootDay, onOpenTalentCalendar, initialTab, onOpenCard, onScheduled }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const canWrite = canAssignTalent(user);       // Producer / Power User / Admin

  // 'assigned' replaced 'scheduled' (2026-09-02); old back tickets may carry 'scheduled', so both land on Assigned.
  const [activeTab,   setActiveTab]   = useState(['scheduled', 'assigned'].includes(initialTab) ? 'assigned' : 'ready');
  const [clients,     setClients]     = useState([]);
  // v2 key (2026-09-03): first visit defaults to All projects
  const [clientId,    setClientId]    = useState(() => localStorage.getItem('sl_client_v2') ?? null);
  const [availableOn, setAvailableOn] = useState('');
  const [showHidden,  setShowHidden]  = useState(false);
  const [q,           setQ]           = useState('');
  const [page,        setPage]        = useState(1);
  const [data,        setData]        = useState({ rows: [], total: 0, pageSize: 50 });
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [planFor,     setPlanFor]     = useState(null); // scriptId being planned
  const [notesEdit,   setNotesEdit]   = useState(null); // { liveProductionId, value }
  const [expandedSeries, setExpandedSeries] = useState(() => new Set());
  const workRef = useRef(null);

  // D-057: the project options come from the data (the API's clients facet for this tab).
  useEffect(() => {
    if (clientId !== null) return;
    api.get('/api/tal?status=ready&pageSize=1').then(r => {
      setClients(r.data.clients || []);
      setClientId(prev => (prev !== null ? prev : ''));
    }).catch(() => setClientId(''));
  }, [clientId]);

  // A jump from the Production page lands on its row's tab, unfiltered, one large page.
  useEffect(() => {
    if (!focus) return;
    setActiveTab(focus.tab === 'ready' ? 'ready' : 'assigned');
    setClientId('');
    setQ('');
    setPage(1);
  }, [focus]);

  const load = useCallback(async () => {
    if (clientId === null) return; // projects not resolved yet
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: activeTab, page: String(page) });
      if (focus) params.set('pageSize', '200');
      if (clientId) params.set('clientId', clientId);
      if (q.trim()) params.set('q', q.trim());
      if (activeTab === 'ready') {
        if (availableOn) params.set('availableOn', availableOn);
        if (showHidden)  params.set('showHidden', '1');
      }
      const r = await api.get(`/api/tal?${params.toString()}`);
      setData(r.data);
      if (Array.isArray(r.data.clients)) setClients(r.data.clients);
    } catch (e) {
      setError(e.response?.data?.error || 'Check your connection, then try again.');
    } finally { setLoading(false); }
  }, [activeTab, clientId, availableOn, showHidden, q, page, focus]);
  useEffect(() => { load(); }, [load]);

  function pickClient(id) {
    setClientId(id);
    setPage(1);
    localStorage.setItem('sl_client_v2', id);
  }

  // The editor gets a return ticket: its back button comes back to this tab of the Script list.
  function openInEditor(scriptId) {
    navigate('/scripts', {
      state: {
        openScriptId: scriptId,
        backTo: { path: '/calendar', state: { calendarInit: { pageTab: 'assign', scriptListTab: activeTab } }, label: 'Script list' },
      },
    });
  }

  async function handleHide(row) {
    const ok = await confirm({
      title: 'Hide this script from the Ready tab?',
      body: `${clean(row.scriptName)} stays schedulable and comes back with Show hidden.`,
      confirmLabel: 'Hide',
    });
    if (!ok) return;
    try { await api.patch(`/api/tal/${row.scriptId}/remove`); load(); }
    catch (e) { toast(clean(e.response?.data?.error) || 'The script was not hidden. Try again.', { tone: 'danger' }); }
  }
  async function handleUnhide(row) {
    try { await api.patch(`/api/tal/${row.scriptId}/restore`); load(); }
    catch (e) { toast(clean(e.response?.data?.error) || 'The script was not returned to Ready. Try again.', { tone: 'danger' }); }
  }
  async function saveNotes() {
    if (!notesEdit) return;
    try {
      await api.patch(`/api/tal/lp/${notesEdit.liveProductionId}/notes`, { notes: notesEdit.value });
      setNotesEdit(null);
      load();
    } catch (e) { toast(clean(e.response?.data?.error) || 'The notes did not save. Try again.', { tone: 'danger' }); }
  }

  const pageSize = data.pageSize || 50;
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  // Render only rows fetched for the active tab (the two row shapes differ; DEF-096).
  const tabRows = useMemo(() => (data.status === activeTab ? data.rows : []), [data, activeTab]);

  // ── Focus jump (D-088): select the row and scroll the Grid to it, then consume the ticket ─────────────────────
  const rowHasFocus = row => !!focus?.liveProductionId && (row.liveProductionId === focus.liveProductionId || (row.episodes || []).some(e => e.liveProductionId === focus.liveProductionId));
  const isSeriesExpanded = row => expandedSeries.has(row.scriptId) || rowHasFocus(row);
  const toggleSeries = scriptId => setExpandedSeries(prev => { const n = new Set(prev); if (n.has(scriptId)) n.delete(scriptId); else n.add(scriptId); return n; });

  // Ready: grouped by stage (Writing = Needs review, Scripted = Approved), API order inside each group.
  const readyGroups = useMemo(() => {
    if (activeTab !== 'ready') return [];
    const present = new Set(tabRows.map(r => stageOf({ qualStatus: r.qualStatus })));
    return READY_STAGES.filter(s => present.has(s)).map(s => ({ key: s, header: <StageChip stage={s} /> }));
  }, [activeTab, tabRows]);

  // Assigned: one row per series (D-120) with its episodes after it when unfolded
  const assignedRows = useMemo(() => {
    if (activeTab !== 'assigned') return [];
    return tabRows.flatMap(row => (row.isSeries && isSeriesExpanded(row)
      ? [row, ...row.episodes.map(ep => ({ ...ep, isEpisode: true, series: row }))]
      : [row]));
  }, [activeTab, tabRows, expandedSeries, focus]); // eslint-disable-line react-hooks/exhaustive-deps
  const assignedKey = r => (r.isSeries ? r.scriptId : r.liveProductionId);

  const focusKey = !focus ? null
    : activeTab === 'ready' ? (focus.scriptId || null)
      : (assignedRows.find(r => !r.isSeries && r.liveProductionId === focus.liveProductionId) ? focus.liveProductionId : null);

  useEffect(() => {
    if (!focus || loading || !focusKey) return undefined;
    let offset = null;
    if (activeTab === 'ready') {
      let y = 0;
      for (const g of readyGroups) {
        y += ROW_PX;
        const rs = tabRows.filter(r => stageOf({ qualStatus: r.qualStatus }) === g.key);
        const i = rs.findIndex(r => r.scriptId === focusKey);
        if (i >= 0) { offset = y + i * ROW_PX; break; }
        y += rs.length * ROW_PX;
      }
    } else {
      const i = assignedRows.findIndex(r => assignedKey(r) === focusKey);
      if (i >= 0) offset = i * ROW_PX;
    }
    if (offset == null) return undefined;
    const grid = workRef.current?.querySelector('.ui-grid');
    if (grid) grid.scrollTop = Math.max(0, offset - grid.clientHeight / 2 + ROW_PX);
    const t = setTimeout(() => onFocusConsumed && onFocusConsumed(), 2500);
    return () => clearTimeout(t);
  }, [focus, loading, data, focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ready grid ──────────────────────────────────────────────────────────────
  const readyColumns = [
    { key: 'title', label: 'Script', width: 280, render: r => (
      <span className="ui-cluster ui-cluster--nowrap" title={canWrite ? `${clean(r.scriptName)}: assign CAM segments and talent` : clean(r.scriptName)}>
        <span className="ui-truncate">{clean(r.scriptName)}</span>{r.hidden && <Tag>Hidden</Tag>}
      </span>
    ) },
    { key: 'stage', label: 'Stage', width: 120, render: r => <StageChip stage={stageOf({ qualStatus: r.qualStatus })} /> },
    { key: 'condition', label: 'Condition', width: 128, render: r => { const c = conditionOf({ qualStatus: r.qualStatus }); return c ? <Badge condition={c} /> : clean(r.qualStatus); } },
    { key: 'client', label: 'Project', width: 150, render: r => (r.client?.name ? <Tag>{clean(r.client.name)}</Tag> : <span className="ui-meta">No project</span>) },
    { key: 'format', label: 'Format', width: 96, render: r => (r.format ? <Tag>{r.format}</Tag> : null) },
    { key: 'talent', label: 'Talent', width: 168, render: r => (r.noTalent
      ? <span className="ui-meta">No talent assigned</span>
      : <span className="ui-cluster ui-cluster--nowrap">{r.resolvedTalents.map(t => (
          <TalentLink key={t.id} label={t.initials || clean(t.name)} title={`${clean(t.name)}: open the Talent calendar`} onClick={onOpenTalentCalendar} />
        ))}</span>) },
    { key: 'writer', label: 'Writer', width: 128, render: r => (r.writer?.name ? clean(r.writer.name) : <span className="ui-meta">No writer</span>) },
    ...(canWrite ? [{ key: 'assign', label: 'Plan', width: 100, render: r => (
      <Button variant="secondary" icon={CalendarPlus} title="Assign CAM segments and talent. The script moves to Assigned."
        onClick={e => { e.stopPropagation(); setPlanFor(r.scriptId); }}>Assign</Button>
    ) }] : []),
  ];
  const readyActions = r => (
    <>
      <Button variant="quiet" iconOnly icon={FileText} label={`Open ${clean(r.scriptName)} in the script editor`} onClick={() => openInEditor(r.scriptId)} />
      {canWrite && !r.hidden && <Button variant="quiet" iconOnly icon={EyeOff} label={`Hide ${clean(r.scriptName)} from the Ready tab`} onClick={() => handleHide(r)} />}
      {canWrite && r.hidden && <Button variant="quiet" icon={Eye} onClick={() => handleUnhide(r)} title="Return this script to the Ready tab">Unhide</Button>}
    </>
  );

  // ── Assigned grid ───────────────────────────────────────────────────────────
  // Run 5 (D-060): a booked talent not covered on a future shoot day shows as a danger badge with the reason.
  function assignedTalent(row) {
    const seen = new Map();
    for (const seg of row.segments) {
      for (const t of seg.talents) {
        if (!t.name) continue;
        const key = t.talentId || `n:${t.name}`;
        if (!seen.has(key)) seen.set(key, { t, brokenDays: [] });
        if (t.covered === false && seg.shootDay) seen.get(key).brokenDays.push({ day: seg.shootDay, cov: t.coverage });
      }
    }
    const chips = [...seen.values()].map(({ t, brokenDays }) => {
      const broken = brokenDays.length > 0;
      const name = clean(t.name);
      return (
        <TalentLink key={t.talentId || `n:${t.name}`} label={t.initials || name} broken={broken} onClick={onOpenTalentCalendar}
          title={broken ? `${brokenDays.map(b => coverageReason(name, `on ${b.day}`, b.cov)).join('\n')}\nRe-time, re-plan or re-cast.` : `${name}: open the Talent calendar`} />
      );
    });
    return chips.length ? <span className="ui-cluster ui-cluster--nowrap">{chips}</span> : <span className="ui-meta">None</span>;
  }
  function episodeTalent(ep) {
    const uniq = [...new Map((ep.segments || []).flatMap(sg => sg.talents || []).map(t => [t.talentId || `n:${t.name}`, t])).values()];
    return (
      <span className="ui-cluster ui-cluster--nowrap">
        {uniq.map(t => (t.covered === false
          ? <Badge key={t.talentId || `n:${t.name}`} tone="danger">{t.initials || clean(t.name)}</Badge>
          : <Tag key={t.talentId || `n:${t.name}`}>{t.initials || clean(t.name)}</Tag>))}
      </span>
    );
  }

  const assignedColumns = [
    { key: 'title', label: 'Script', width: 320, render: r => {
      if (r.isEpisode) {
        return (
          <span className="ui-cluster ui-cluster--nowrap" data-testid="sl-episode-row" title={`${clean(r.series.title)}, Ep ${r.episodeNumber}: open the production`}>
            <span className="ui-indent" aria-hidden="true" />
            <span className="ui-meta ui-num">Ep {r.episodeNumber}</span>
            <span className="ui-truncate">{clean(r.episodeTitle) || `Episode ${r.episodeNumber}`}</span>
          </span>
        );
      }
      if (r.isSeries) {
        const open = isSeriesExpanded(r);
        return (
          <span className="ui-cluster ui-cluster--nowrap" data-testid="sl-series-row" data-episodes={r.episodeCount}
            title={`${clean(r.title)}: ${plural(r.episodeCount, 'episode')} assigned. Click to ${open ? 'fold' : 'unfold'}.`}>
            <Icon icon={open ? ChevronDown : ChevronRight} label={open ? 'Unfolded' : 'Folded'} />
            <span className="ui-truncate">{clean(r.title)}</span>
            <Tag>Longform</Tag>
            <span className="ui-meta">{plural(r.episodeCount, 'episode')}</span>
          </span>
        );
      }
      return (
        <span className="ui-cluster ui-cluster--nowrap" title={`${clean(r.title)}: open the production`}>
          <span className="ui-truncate">{clean(r.title)}</span>{r.isRiff && <Tag>Riff</Tag>}
        </span>
      );
    } },
    { key: 'client', label: 'Project', width: 150, render: r => {
      const c = r.isEpisode ? r.series.client : r.client;
      return c?.name ? <Tag>{clean(c.name)}</Tag> : <span className="ui-meta">No project</span>;
    } },
    { key: 'days', label: 'Shoot days', width: 220, render: r => (
      <span className="ui-cluster ui-cluster--nowrap">
        {r.filmedCount > 0 && (
          <span title={r.isEpisode ? undefined : `${r.filmedCount} of ${r.totalSegments} segments filmed. The script leaves Assigned when all are filmed.`}>
            <Tag>{r.filmedCount} of {r.totalSegments} filmed</Tag>
          </span>
        )}
        {r.shootDays.length === 0
          ? (r.isEpisode
            ? <span className="ui-meta">No shoot day set</span>
            : <span title="Segments are assigned but no shoot day is set yet. Book talent on the Talent calendar, then schedule from the day's shootable scripts."><Badge tone="warn">No shoot day set</Badge></span>)
          : <ShootDays days={r.shootDays} onOpenShootDay={onOpenShootDay} />}
      </span>
    ) },
    { key: 'talent', label: 'Talent', width: 196, render: r => (r.isEpisode ? episodeTalent(r) : assignedTalent(r)) },
    { key: 'notes', label: 'Notes', width: 280, render: r => {
      if (r.isSeries) return <span className="ui-meta">Notes live on each episode</span>;
      if (r.isEpisode) return r.notes ? <span title={clean(r.notes)}>{clean(r.notes)}</span> : null;
      // D-056: any internal user edits notes here (the server refuses externals). On a Riff card this IS its title.
      if (notesEdit?.liveProductionId === r.liveProductionId) {
        return (
          <span className="ui-grow" onClick={stop} onKeyDown={stop}>
            <Input aria-label={r.isRiff ? 'Riff title' : 'Notes'} autoFocus value={notesEdit.value}
              onChange={e => setNotesEdit({ ...notesEdit, value: e.target.value })}
              onBlur={saveNotes}
              onKeyDown={e => { if (e.key === 'Enter') saveNotes(); if (e.key === 'Escape') setNotesEdit(null); }} />
          </span>
        );
      }
      return (
        <button type="button" className={cx('ui-link ui-truncate', !r.notes && 'ui-text-3')}
          title={r.isRiff ? 'Riff cards keep their title here: editing this edits the card title' : 'Click to edit the notes (the Production page’s Notes)'}
          onClick={e => { e.stopPropagation(); setNotesEdit({ liveProductionId: r.liveProductionId, value: r.notes || '' }); }}>
          {r.isRiff && <Tag>Title</Tag>}{r.notes ? clean(r.notes) : 'Add notes'}
        </button>
      );
    } },
  ];

  const tabItems = [
    { id: 'ready', label: 'Ready', count: activeTab === 'ready' && !loading && data.status === 'ready' ? data.total : undefined },
    { id: 'assigned', label: 'Assigned', count: activeTab === 'assigned' && !loading && data.status === 'assigned' ? data.total : undefined },
  ];
  const gridState = error ? 'error' : loading ? 'loading' : 'data';
  const gridError = { title: 'The Script list did not load', message: clean(error), onRetry: load };

  return (
    <>
      <div className="ui-inset ui-inset--bar ui-toolbar">
        <Tabs label="Script list" value={activeTab} items={tabItems} onChange={k => { setActiveTab(k); setPage(1); }} />
        <Select aria-label="Project" value={clientId || ''} onChange={e => pickClient(e.target.value)}
          options={[
            { value: '', label: 'All projects' },
            ...clients.map(c => ({ value: c.id, label: clean(c.name) })),
            // keep the current pick selectable when this tab has no rows for it
            ...(clientId && !clients.some(c => c.id === clientId) ? [{ value: clientId, label: 'Current project (no rows in this tab)' }] : []),
          ]} />
        <Input aria-label="Search titles" icon={Search} placeholder="Search titles" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
        {activeTab === 'ready' && (
          <>
            <span className="ui-meta" title="Keep scripts whose resolved talent are all available on this date. Scripts with no talent assigned always stay listed.">Talent available on</span>
            <DatePicker aria-label="Talent available on" value={availableOn} onChange={e => { setAvailableOn(e.target.value); setPage(1); }} />
            {availableOn && <Button variant="quiet" icon={X} onClick={() => { setAvailableOn(''); setPage(1); }}>Clear</Button>}
            <Checkbox label="Show hidden" checked={showHidden} onChange={e => { setShowHidden(e.target.checked); setPage(1); }}
              title="Include scripts hidden from Ready so they can be recovered" />
          </>
        )}
        <span className="ui-spacer" />
        <Button variant="quiet" icon={RefreshCw} onClick={load}>Refresh</Button>
      </div>

      <div className="ui-work" ref={workRef}>
        {activeTab === 'ready' ? (
          <Grid key="ready" label="Ready scripts" density="medium" storageKey="hf-grid:calendar-ready"
            columns={readyColumns} rows={tabRows} rowKey={r => r.scriptId}
            groups={readyGroups} groupOf={r => stageOf({ qualStatus: r.qualStatus })}
            selectedKey={planFor || focusKey} mutedRow={r => r.hidden}
            onOpen={canWrite ? (r => setPlanFor(r.scriptId)) : undefined}
            rowActions={readyActions} state={gridState} error={gridError}
            empty={{ message: 'No script is Ready for this filter. Approved Skit and Mockumentary scripts show here until their CAM segments are assigned.' }} />
        ) : (
          <Grid key="assigned" label="Assigned scripts" density="medium" storageKey="hf-grid:calendar-assigned"
            columns={assignedColumns} rows={assignedRows} rowKey={assignedKey} selectedKey={focusKey}
            onOpen={r => { if (r.isSeries) toggleSeries(r.scriptId); else if (onOpenCard) onOpenCard(r.liveProductionId); }}
            state={gridState} error={gridError}
            empty={{ message: 'No script is Assigned for this filter. Confirm a plan from the Ready tab and the script lands here.' }} />
        )}
      </div>

      {!loading && data.total > pageSize && (
        <div className="ui-inset ui-inset--bar ui-toolbar">
          <span className="ui-spacer" />
          <Button variant="quiet" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="ui-meta ui-num">Page {page} of {totalPages}, {plural(data.total, 'script')}</span>
          <Button variant="quiet" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<Icon icon={ChevronRight} /></Button>
          <span className="ui-spacer" />
        </div>
      )}

      {planFor && (
        <SchedulePlanModal scriptId={planFor} onClose={() => setPlanFor(null)}
          onConfirmed={() => {
            setPlanFor(null);
            load();
            if (onScheduled) onScheduled();   // the new segments reach the Shoot schedule's records too
          }} />
      )}
    </>
  );
}
